import { useEffect, useState } from 'react'
import { parseTags } from '../data/tags'
import type { PeriodNoteValue } from '../data/periodNotes'

interface Props {
  label: string
  value: PeriodNoteValue
  onSave: (value: PeriodNoteValue) => void
}

/**
 * A description and tags for a whole day or a whole week — the things that
 * belong to the period rather than to any one block ("release week", "on call").
 */
export function PeriodNoteBar({ label, value, onSave }: Props) {
  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(value.note ?? '')
  const [tags, setTags] = useState(value.tags.join(', '))

  useEffect(() => {
    if (editing) return
    setNote(value.note ?? '')
    setTags(value.tags.join(', '))
  }, [value, editing])

  function commit() {
    setEditing(false)
    const next = { note: note.trim() || null, tags: parseTags(tags) }
    if (next.note !== value.note || next.tags.join(',') !== value.tags.join(',')) onSave(next)
  }

  if (!editing) {
    const empty = !value.note && value.tags.length === 0
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="flex w-full items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-left transition-colors duration-150 hover:border-line-strong"
      >
        <span className="shrink-0 text-[11px] font-medium tracking-wide text-faint uppercase">{label}</span>
        {empty ? (
          <span className="text-[13px] text-faint">Add a description or tags</span>
        ) : (
          <>
            {value.note ? <span className="truncate text-[13px] text-text">{value.note}</span> : null}
            {value.tags.map((tag) => (
              <span
                key={tag}
                className="shrink-0 rounded border border-line-strong px-1.5 py-px text-[11px] text-dim"
              >
                {tag}
              </span>
            ))}
          </>
        )}
      </button>
    )
  }

  return (
    <div className="rounded-lg border border-line-strong bg-surface p-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          autoFocus
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit()
            if (event.key === 'Escape') setEditing(false)
          }}
          placeholder={`What made this ${label.toLowerCase()} what it was?`}
          className="min-w-0 flex-1 rounded-md border border-line bg-bg px-2.5 py-1.5 text-[14px] outline-none focus:border-line-strong"
        />
        <input
          value={tags}
          onChange={(event) => setTags(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit()
            if (event.key === 'Escape') setEditing(false)
          }}
          placeholder="tags, comma separated"
          className="w-full rounded-md border border-line bg-bg px-2.5 py-1.5 text-[14px] outline-none focus:border-line-strong sm:w-56"
        />
        <button
          type="button"
          onClick={commit}
          className="rounded-md bg-text px-3 py-1.5 text-[14px] font-semibold text-bg"
        >
          Save
        </button>
      </div>
    </div>
  )
}
